import { createTheme } from "@mui/material";
import borders from "./basics/borders";
import boxShadows from "./basics/boxShadows";
import breakpoints from "./basics/breakpoints";
import colors from "./basics/colors";
import darkColors from "./basics/darkColors";
import typography from "./basics/typography";

import hexToRgb from "../Functions/hexToRgb";
import linearGradient from "../Functions/linearGradient";
import pxToRem from "../Functions/pxToRem";
import rgba from "../Functions/rgba";
import boxShadow from "../Functions/boxShadow";
import inputOutlined from "./components/input/inputOutlined";
import input from "./components/input/input";
import inputLabel from "./components/input/inputLabel";
import button from "./components/button";
import switchButton from "./components/switchButton";
// import drawer from "./components/drawer";
import divider from "./components/divider";
import list from "./components/list";
import listItem from "./components/list/listItem";
import listItemText from "./components/list/listItemText";
import icon from "./components/icon";
import link from "./components/link";
import avatar from "./components/avatar";
import appBar from "./components/appBar";
import card from "./components/card";
import cardMedia from "./components/card/cardMedia";
import cardContent from "./components/card/cardContent";
import iconButton from "./components/iconButton";
import { table, tableHead, tableCell, tableContainer } from "./components/table";
import autocomplete from "./components/form/autocomplete";
import checkbox from "./components/form/checkbox";

// Create theme function that supports dark mode
export const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  const paletteColors = isDark ? darkColors : colors;

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      ...paletteColors,
    },
    breakpoints: { ...breakpoints },
    typography: { ...typography },
    boxShadows: { ...boxShadows },
    borders: { ...borders },
    functions: {
      boxShadow,
      hexToRgb,
      linearGradient,
      pxToRem,
      rgba,
    },
    components: {
      // MuiDrawer: { ...drawer },
      MuiOutlinedInput: { ...inputOutlined },
      MuiInput: { ...input },
      MuiInputLabel: { ...inputLabel },
      MuiButton: { ...button },
      MuiSwitch: { ...switchButton },
      MuiDivider: { ...divider },
      MuiAvatar: { ...avatar },
      MuiList: { ...list },
      MuiListItem: { ...listItem },
      MuiListItemText: { ...listItemText },
      MuiIcon: { ...icon },
      MuiLink: { ...link },
      MuiAppBar: { ...appBar },
      MuiCard: {
        ...card,
        styleOverrides: {
          ...card.styleOverrides,
          root: {
            ...card.styleOverrides?.root,
            backgroundColor: isDark ? '#181b1f' : '#ffffff',
            borderColor: isDark ? '#2c3039' : '#e0e0e0',
          },
        },
      },
      MuiCardMedia: { ...cardMedia },
      MuiCardContent: { ...cardContent },
      MuiIconButton: { ...iconButton },
      MuiTable: { ...table },
      MuiTableHead: {
        ...tableHead,
        styleOverrides: {
          ...tableHead.styleOverrides,
          root: {
            ...tableHead.styleOverrides?.root,
            backgroundColor: isDark ? '#22252b' : '#f5f5f5',
          },
        },
      },
      MuiTableContainer: { ...tableContainer },
      MuiAutocomplete: { ...autocomplete },
      MuiCheckbox: { ...checkbox },
      MuiTableCell: {
        ...tableCell,
        styleOverrides: {
          ...tableCell.styleOverrides,
          root: {
            ...tableCell.styleOverrides?.root,
            borderColor: isDark ? '#2c3039' : 'rgba(224, 224, 224, 1)',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            "&:-webkit-autofill": {
              WebkitBoxShadow: isDark ? '0 0 0 100px #181b1f inset' : '0 0 0 100px #fff inset',
              WebkitTextFillColor: isDark ? '#e9edf2' : '#333',
              caretColor: isDark ? '#e9edf2' : '#333',
              transitionDelay: "9999s",
              transitionProperty: "background-color, color",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#181b1f' : '#ffffff',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#22252b' : undefined,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#22252b' : undefined,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#111217' : '#f0f2f5',
            color: isDark ? '#e9edf2' : '#344767',
          },
          ...(isDark ? {
            'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus, select:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 100px #181b1f inset !important',
              WebkitTextFillColor: '#e9edf2 !important',
              caretColor: '#e9edf2 !important',
              borderColor: '#3d4249 !important',
            },
          } : {}),
        },
      },
    },
  });
};

// Default light theme for backward compatibility
const theme = createAppTheme('light');

export default theme;
